import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import api from "../api";
import { COMPANIES, HEADER_COMPANY_KEY } from "../companies";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExp, setSessionExp] = useState(null); // epoch (secondi) di scadenza del token di sessione
  const [brandCompany, setBrandCompanyState] = useState(() => {
    const c = localStorage.getItem(HEADER_COMPANY_KEY);
    return COMPANIES[c] ? c : null;
  });

  const setBrandCompany = (code) => {
    if (!COMPANIES[code]) return;
    localStorage.setItem(HEADER_COMPANY_KEY, code);
    setBrandCompanyState(code);
  };

  useEffect(() => {
    // Verifica la sessione tramite cookie httpOnly (inviato automaticamente).
    api
      .get("/auth/me")
      .then((res) => { setUser(res.data); setSessionExp(res.data?.token_exp ?? null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = async (username, password, company, remember = true) => {
    try {
      const res = await api.post("/auth/login", { username, password, company, remember });
      setUser(res.data.user);
      setSessionExp(res.data?.token_exp ?? null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Errore di accesso. Riprova." };
    }
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    setUser(null);
    setSessionExp(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      setSessionExp(res.data?.token_exp ?? null);
    } catch (e) {
      console.error("refreshUser failed", e);
    }
  };

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "responsabile";
  const isApprover = isAdmin || isManager;
  const isExternal = !!user?.is_external;
  // Dipendente "base": ruolo dipendente (né admin né responsabile). Alcune funzioni sono nascoste a questo livello.
  const isEmployee = user?.role === "dipendente";

  // Header branding: admin-selected company override, else the user's own company.
  const effectiveBrand = brandCompany || user?.company || "teleborsa";

  const value = useMemo(
    () => ({ user, loading, sessionExp, login, logout, refreshUser, isAdmin, isManager, isApprover, isExternal, isEmployee, effectiveBrand, brandCompany, setBrandCompany }),
    [user, loading, sessionExp, isAdmin, isManager, isApprover, isExternal, isEmployee, effectiveBrand, brandCompany]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
